import React, { useMemo, useState } from "react";

import { SERIES_COLORS } from "../../constants/chartTheme";
import type { ForecastCurvePoint } from "../../types";
import { formatINR } from "../../utils/format";
import "./ScenarioForecastPanel.css";

type Props = {
  points: ForecastCurvePoint[];
  loading?: boolean;
  forecastModel?: string;
};

/** The wireframe's three fixed rows. */
const FIXED_SCENARIOS = [25, 50, 70];
const DEFAULT_CUSTOM = 85;

function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export const ScenarioForecastPanel: React.FC<Props> = ({ points, loading, forecastModel }) => {
  const [customPct, setCustomPct] = useState(DEFAULT_CUSTOM);
  // Free-text buffer so a user can clear the field and type without it
  // snapping back to 0 on the first keystroke.
  const [customInput, setCustomInput] = useState(String(DEFAULT_CUSTOM));

  const byPct = useMemo(() => {
    const map = new Map<number, ForecastCurvePoint>();
    points.forEach((point) => map.set(Math.round(point.sell_through), point));
    return map;
  }, [points]);

  const maxRevenue = useMemo(
    () => points.reduce((max, point) => Math.max(max, point.revenue), 0),
    [points]
  );

  const applyPct = (raw: number) => {
    const next = clampPct(raw);
    setCustomPct(next);
    setCustomInput(String(next));
  };

  if (loading) {
    return <p className="eventos-subtle">Loading scenarios…</p>;
  }
  if (points.length === 0) {
    return (
      <p className="eventos-subtle">
        Add ticket tiers to model sell-through scenarios.
      </p>
    );
  }

  const renderRow = (pct: number, key: string) => {
    const point = byPct.get(pct);
    if (!point) return null;
    const width = maxRevenue > 0 ? (point.revenue / maxRevenue) * 100 : 0;
    return (
      <div className="scenario-forecast__row" key={key}>
        <span className="scenario-forecast__pct">{pct}%</span>
        <div className="scenario-forecast__track">
          <div
            className="scenario-forecast__fill"
            style={{
              width: `${width}%`,
              background: point.is_break_even ? SERIES_COLORS.profit : SERIES_COLORS.revenue,
            }}
          />
        </div>
        <span className="scenario-forecast__value">
          {formatINR(point.revenue)}
          <span className="eventos-subtle scenario-forecast__tickets">
            {" "}
            · {point.tickets_sold.toLocaleString("en-IN")} tickets
          </span>
        </span>
      </div>
    );
  };

  const customPoint = byPct.get(customPct);

  return (
    <div className="scenario-forecast">
      {FIXED_SCENARIOS.map((pct) => renderRow(pct, `fixed-${pct}`))}

      <div className="scenario-forecast__custom">
        <label className="scenario-forecast__custom-field">
          <span className="visually-hidden">Custom sell-through percentage</span>
          <input
            className="eventos-input scenario-forecast__input"
            type="number"
            min={0}
            max={100}
            value={customInput}
            onChange={(e) => {
              setCustomInput(e.target.value);
              if (e.target.value !== "") applyPct(Number(e.target.value));
            }}
            onBlur={() => applyPct(Number(customInput || 0))}
            aria-label="Custom sell-through percentage"
          />
          <span className="scenario-forecast__unit">%</span>
        </label>

        <div className="scenario-forecast__custom-body">
          <input
            className="scenario-forecast__slider"
            type="range"
            min={0}
            max={100}
            step={1}
            value={customPct}
            onChange={(e) => applyPct(Number(e.target.value))}
            aria-label="Custom sell-through slider"
          />
          {customPoint ? (
            <div className="scenario-forecast__custom-readout">
              <span className="scenario-forecast__value">{formatINR(customPoint.revenue)}</span>
              <span className="eventos-subtle">
                {customPoint.tickets_sold.toLocaleString("en-IN")} tickets ·{" "}
                {customPoint.profit >= 0 ? "Profit" : "Loss"} {formatINR(Math.abs(customPoint.profit))}
                {customPoint.is_break_even ? " · breaks even" : ""}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {forecastModel ? (
        <p
          className="eventos-subtle scenario-forecast__model"
          title="Cheaper tiers fill before premium tiers at each sell-through level. A 50% sell-through fills the cheapest 50% of inventory, not 50% of every tier."
        >
          Cheapest-tier-first model
        </p>
      ) : null}
    </div>
  );
};

export default ScenarioForecastPanel;
